import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { FileSpreadsheet, Loader2, X } from 'lucide-react';
import { fetchBuilt } from './TagSavedLists';
import { tagThemeClass } from './tagTheme';
import { getPricing, setPricing, ensureRates, pricingOn } from '@/lib/tagPricing';
import { RateCardPicker, rateCardById } from '@/lib/rateCards';
import { downloadTagsExcel } from '@/lib/tagExcel';
import { tagMaterials, buildTagDataFromDetail } from '@/components/jewellery/JewelleryTagViewer';
import { notesApi } from '@/lib/notesApi';
import { TagMatOptToggles } from '@/components/jewellery/TagMatOptToggles';
import { extractError } from '@/lib/errors';

const KEY = 'tagws_selection_excel';
const SUMS = [['none', 'No summary'], ['weights', 'Gross & Net'], ['full', 'With raw materials']];
const FORMATS = [['expanded', 'Expanded'], ['collapsed', 'Collapsed'], ['raw', 'Raw']];
const LABEL = 'mb-1 block text-[length:calc(var(--fs,1)*0.6875rem)] font-semibold uppercase tracking-[0.12em] text-muted-foreground';
const seg = (on) => `h-9 flex-1 rounded-lg border px-2 text-xs font-semibold transition-colors ${on ? 'border-[hsl(var(--ring))] bg-[hsl(var(--ring))]/10 text-foreground' : 'border-border bg-background text-muted-foreground hover:bg-secondary'}`;

function Seg({ options, value, onChange, testid }) {
  return (
    <div className="flex gap-1.5" data-testid={testid}>
      {options.map(([id, label]) => <button key={id} type="button" onClick={() => onChange(id)} aria-pressed={value === id} className={seg(value === id)} data-testid={`${testid}-${id}`}>{label}</button>)}
    </div>
  );
}

// Excel of the selection — same file as Saved Lists / batch printing; prices only for rate-card users.
export function SelectionExcelDialog({ selected, onClose }) {
  const canView = getPricing().canView;
  const fallback = `Selected pieces (${selected.length})`;
  const saved = (() => { try { return JSON.parse(localStorage.getItem(KEY) || '{}'); } catch { return {}; } })();
  const [title, setTitle] = useState(() => localStorage.getItem('tagws_selection_title') || '');
  const [fmt, setFmt] = useState(saved.fmt || 'expanded');
  const [pieces, setPieces] = useState(saved.pieces !== false);
  const [prices, setPrices] = useState(canView && !!saved.prices);
  const [card, setCard] = useState(getPricing().cardId || saved.card || '');
  const [summary, setSummary] = useState(saved.summary || 'full');
  const [busy, setBusy] = useState(false);
  const repaired = selected.filter((x) => x.repair?.last_repaired_at).length;
  const [old, setOld] = useState(false);
  useEffect(() => {
    const k = (e) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [busy, onClose]);
  const go = async () => {
    if (prices && !card) { toast.error('Choose a rate card for prices.'); return; }
    const name = title.trim() || fallback;
    localStorage.setItem('tagws_selection_title', title.trim());
    localStorage.setItem(KEY, JSON.stringify({ fmt, pieces, prices, card, summary }));
    const prev = getPricing();
    setBusy(true);
    try {
      let built = await fetchBuilt(selected.map((x) => x.id));
      if (!built.length) throw new Error('None of the selected pieces could be loaded.');
      if (old && repaired) {
        // On demand: swap in each repaired piece's details from before its latest repair.
        const { data } = await notesApi.post('/notes/tag-workspace/pieces/repairs/before', { ids: built.map((d) => d.id) });
        const bef = new Map(data.items.map((it) => [it.id, buildTagDataFromDetail(it)]));
        built = built.map((d) => bef.get(d.id) || d);
      }
      if (canView) setPricing({ mode: prices ? (prev.mode === 'none' ? 'total' : prev.mode) : 'none', cardId: prices ? card : prev.cardId });
      if (!(await ensureRates(built, 'page'))) return;
      await downloadTagsExcel(built.map((d) => ({ data: d, materials: tagMaterials(d, fmt) })), {
        rmMode: fmt, showPieces: pieces, withRates: pricingOn(), rates: rateCardById(getPricing().cardId), fileName: `${name}.xlsx`, listName: name, summaryKind: summary,
      });
      toast.success('Excel downloaded.');
      onClose();
    } catch (e) { toast.error(extractError(e, e?.message || 'Could not build the Excel file.')); } finally {
      if (canView) setPricing({ mode: prev.mode, cardId: prev.cardId });
      setBusy(false);
    }
  };
  return createPortal(
    <div className={`tag-scope ${tagThemeClass()} fixed inset-0 z-[300] flex items-end justify-center sm:items-center sm:p-3`} data-testid="tagws-selection-excel-dialog">
      <div className="absolute inset-0 bg-black/50" onClick={() => !busy && onClose()} />
      <div className="relative z-10 max-h-[92vh] w-full overflow-y-auto rounded-t-3xl border border-border bg-card p-5 shadow-2xl animate-in fade-in slide-in-from-bottom-2 duration-200 sm:max-w-md sm:rounded-2xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="flex items-center gap-2 font-semibold text-foreground"><FileSpreadsheet size={17} className="text-emerald-600" /> Excel of the selection</h3>
          <button onClick={onClose} disabled={busy} aria-label="Close" data-testid="tagws-selection-excel-close"><X size={18} /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className={LABEL} htmlFor="tagws-sel-excel-title">Title</label>
            <input id="tagws-sel-excel-title" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={80} placeholder={fallback}
              className="h-10 w-full rounded-lg border border-input bg-background px-3 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]" data-testid="tagws-selection-excel-title" />
          </div>
          {repaired ? (
            <label className="flex cursor-pointer items-start gap-2 rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-foreground" data-testid="tagws-selection-excel-old">
              <input type="checkbox" checked={old} onChange={(e) => setOld(e.target.checked)} className="mt-0.5 h-4 w-4 accent-amber-600" data-testid="tagws-selection-excel-old-checkbox" />
              <span><span className="font-semibold">Use details before the last repair</span><span className="block text-muted-foreground">{repaired} repaired piece{repaired === 1 ? '' : 's'} in this selection. Off: the new details (after repair) are used.</span></span>
            </label>
          ) : null}
          <div>
            <span className={LABEL}>Raw materials</span>
            <Seg options={FORMATS} value={fmt} onChange={setFmt} testid="tagws-selection-excel-format" />
          </div>
          <div>
            <span className={LABEL}>Pieces</span>
            <Seg options={[['with', 'With pieces'], ['without', 'Without pieces']]} value={pieces ? 'with' : 'without'} onChange={(v) => setPieces(v === 'with')} testid="tagws-selection-excel-pieces" />
          </div>
          {canView ? (
            <div>
              <span className={LABEL}>Prices</span>
              <Seg options={[['no', 'Without prices'], ['yes', 'With prices']]} value={prices ? 'yes' : 'no'} onChange={(v) => setPrices(v === 'yes')} testid="tagws-selection-excel-prices" />
              {prices ? <div className="mt-2"><RateCardPicker value={card} onChange={setCard} className="!h-10 !max-w-none w-full" testid="tagws-selection-excel-rate-card" /></div> : null}
            </div>
          ) : null}
          <div>
            <span className={LABEL}>Summary at the end</span>
            <Seg options={SUMS} value={summary} onChange={setSummary} testid="tagws-selection-excel-summary" />
          </div>
          <TagMatOptToggles testid="tagws-selection-excel-mat-opts" pieces={selected} />
        </div>
        <button type="button" onClick={go} disabled={busy} className="mt-5 inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-emerald-700 text-sm font-semibold text-white hover:bg-emerald-800 disabled:opacity-60" data-testid="tagws-selection-excel-download">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <FileSpreadsheet size={15} />} Download Excel
        </button>
      </div>
    </div>,
    document.body,
  );
}
